import { FC, useMemo, useState } from 'react'
import { MUITable, defineColumns } from 'components/common/MUITable/MUITable.component'
import { Stack, TableCell, Typography } from '@mui/material'
import { WorkspaceRole, WorkspaceSeatType, WorkspaceUserProfile } from 'models/user'
import { useWorkspaceInvitationActions, useWorkspaceList } from 'utils/hooks'

import { BEIGE_400 } from 'constants/styling/theme'
import { LeaveWorkspacePopup } from '../LeaveWorkspacePopup/LeaveWorkspacePopup.component'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { NameAvatar } from 'components/common/Avatars'
import { useTranslation } from 'react-i18next'

/**
 * Renders a table listing workspaces the user is a member of.
 *
 * @remarks
 * - Uses {@link MUITable} with strongly typed column definitions
 * - Displays workspace name, user role, access level, and actions
 * - Supports accept/decline for pending invitation workspaces
 * - Columns are memoized and re-created only on language change
 */
export const WorkspaceList: FC = () => {
  const { t, i18n } = useTranslation(['user_workspace_list', 'workspace_profile', 'actions'])


  const { isWorkspacePending, activeWorkspaceList } = useWorkspaceList()

  const { handleDecline, handleAccept, isDeclinePending, isAcceptPending } = useWorkspaceInvitationActions()

  const [leaveWorkspace, setLeaveWorkspace] = useState<WorkspaceUserProfile | undefined>()

  /**
   * Column configuration for the workspace table.
   *
   * @remarks
   * - Memoized to avoid unnecessary re-renders
   * - Recomputed only when language changes (translations)
   * - Uses {@link defineColumns} to preserve strong typing
   */
  const columnDefinition = useMemo(() => {
    const cols = defineColumns<WorkspaceUserProfile>()([
      {
        name: 'WORKSPACE_NAME',
        translation: t('workspace_name_column'),
        isSortable: false,
        size: '45%',
        mobileSize: '20rem',
        renderCell: ({ id, data }) => {
          const isPending = isWorkspacePending(data)

          return (
            <TableCell key={`${id}_NAME`}>
              <Stack direction="row" gap={1} alignItems="center">

                <NameAvatar borderColor={BEIGE_400} elevation="none" name={data.name} />

                <Typography variant="text-sm" fontWeight="bold">
                  {data.name}
                </Typography>

                {isPending &&
                  <MUIBadge color="blue" size="sm" label={t('workspace_profile:pending_invitation')} sx={{ flexShrink: 0 }} />
                }

              </Stack>
            </TableCell>
          )
        }
      },
      {
        name: 'ROLE',
        translation: t('role_column'),
        isSortable: false,
        size: '20%',
        mobileSize: '15rem',
        renderCell: ({ id, data }) => (
          <TableCell key={`${id}_ROLE`}>
            <Typography variant="text-sm">
              {data.role ? t(`workspace_profile:role.${data.role}`) : '-'}
            </Typography>
          </TableCell>
        )
      },
      {
        name: 'SEAT_TYPE',
        translation: t('seat_type_column'),
        isSortable: false,
        size: '20%',
        mobileSize: '15rem',
        renderCell: ({ id, data }) => (
          <TableCell key={`${id}_SEAT_TYPE`}>
            <Typography variant="text-sm">
              {data.seatType === WorkspaceSeatType.SPONSORED
                ? t('workspace_profile:sponsored_access')
                : data.seatType === WorkspaceSeatType.RESTRICTED
                  ? t('workspace_profile:restricted_access')
                  : t('workspace_profile:full_access')
              }
            </Typography>
          </TableCell>
        )
      },
      {
        name: 'ACTIONS',
        translation: '',
        isSortable: false,
        size: '30%',
        mobileSize: '25rem',
        renderCell: ({ id, data }) => {
          const isPending = isWorkspacePending(data)
          const canLeaveWorkspace = !isPending && data.canLeave

          return (
            <TableCell key={`${id}_ACTIONS`}>
              <Stack direction="row" gap=".8rem" justifyContent="flex-end">

                {isPending &&
                  <>
                    <MUIButton
                      type="secondaryBorder"
                      size="sm"
                      borderStyle="rounded"
                      isLoading={isDeclinePending(data.invitationId)}
                      onClick={() => handleDecline({ workspaceId: data.id, invitationId: data.invitationId })}
                    >
                      {t('actions:Decline')}
                    </MUIButton>

                    <MUIButton
                      type="secondaryBorder"
                      size="sm"
                      isLoading={isAcceptPending(data.invitationId)}
                      onClick={() => handleAccept({ workspaceId: data.id, invitationId: data.invitationId })}
                    >
                      {t('actions:Accept')}
                    </MUIButton>
                  </>
                }

                {canLeaveWorkspace &&
                  <MUIButton
                    type="orangeSecondaryBorder"
                    variant="outlined"
                    size="sm"
                    borderStyle="rounded"
                    onClick={() => setLeaveWorkspace(data)}
                  >
                    {t('leave_workspace')}
                  </MUIButton>
                }

              </Stack>
            </TableCell>
          )
        }
      },
    ])
    return cols
    /**
     * Intentionally limited dependency array for performance.
     * - t: identity follows i18n.language
     * - isWorkspacePending, getDisplayRole: stable (useCallback with [])
     * - handleDecline, handleAccept: stable (useCallback with stable .mutate refs)
     * - setLeaveWorkspace: React setState, always stable
     * Only isDeclinePending/isAcceptPending are included because they carry
     * mutation-pending state that must trigger column re-creation for spinners.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, isDeclinePending, isAcceptPending])

  return (
    <>
      <MUITable<WorkspaceUserProfile, typeof columnDefinition>
        columns={columnDefinition}
        data={activeWorkspaceList.map((workspace) => ({ id: workspace.id, data: workspace })) ?? []}
      />

      {leaveWorkspace &&
        <LeaveWorkspacePopup
          isOpen={!!leaveWorkspace}
          workspace={leaveWorkspace}
          onClose={() => setLeaveWorkspace(undefined)}
        />
      }
    </>
  )
}
